import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { FileText, Zap, Download, Palette, Type, Save, Sparkles, Shield } from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      q: "How long does it take to build a CV on CVForge?",
      a: "Most users complete a polished, download-ready CV in 10–15 minutes. You can start from scratch or load a previously saved draft using your email and 6-digit access code."
    },
    {
      q: "Is CVForge free to use?",
      a: "Building and previewing your CV is completely free. You only pay a small one-time fee to download the final print-ready PDF — no subscription, no hidden charges."
    },
    {
      q: "Do you accept South African payments?",
      a: "Yes. Payments are processed securely in ZAR via PayFast, South Africa's most trusted online payment gateway. International cards work too."
    },
    {
      q: "Can I save my CV and come back later?",
      a: "Yes. Save your draft with your email and we'll email you a 6-digit access code. You can save multiple CV versions and load them on any device."
    },
    {
      q: "Will my downloaded PDF work on any device?",
      a: "Yes. Every PDF is generated server-side and works in Adobe Reader, Preview, Chrome, Edge, and any modern PDF viewer. It also prints cleanly at A4."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Logo (branding wordmark — not an h1) */}
      <header className="py-6 px-6 border-b border-border/40" role="banner">
        <div className="max-w-7xl mx-auto flex items-center">
          <img src="/logo.svg" alt="Instant CVForge logo" className="w-10 h-10 mr-3" width="40" height="40" />
          <div>
            <span className="font-heading text-2xl font-semibold tracking-tight block" aria-label="CVForge">CVForge</span>
            <span className="text-xs text-muted-foreground">Professional CV Builder</span>
          </div>
        </div>
      </header>

      {/* Hero Section (single H1 on the page) */}
      <section className="py-24 px-6" aria-labelledby="hero-heading">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 id="hero-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground">
                  Forge Your
                  <br />
                  <span className="font-semibold">Professional CV</span>
                </h1>
                <p className="text-base sm:text-lg text-muted-foreground font-body max-w-lg">
                  Build a structured, elegant, print-ready CV in minutes. Trusted by South African job seekers and professionals worldwide. 7 templates, 5 fonts, custom colors — no design skills needed.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  data-testid="get-started-btn"
                  onClick={() => navigate("/builder")}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none h-12 px-8 font-medium tracking-wide transition-transform hover:-translate-y-0.5 active:scale-95"
                >
                  Start Building
                </Button>
                <span className="text-xs text-muted-foreground self-center">
                  One-time payment · Instant download · Secure PayFast checkout
                </span>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/19238352/pexels-photo-19238352.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional workspace where a job seeker builds their CV online"
                className="w-full h-[400px] object-cover shadow-sm"
                loading="eager"
                width="940"
                height="400"
              />
              <div className="absolute -bottom-8 -left-8 w-48 h-64 bg-card shadow-2xl p-6 hidden md:block" aria-hidden="true">
                <div className="text-xs text-muted-foreground mb-2">PREVIEW</div>
                <div className="space-y-2">
                  <div className="h-4 bg-foreground w-3/4"></div>
                  <div className="h-2 bg-muted w-full"></div>
                  <div className="h-2 bg-muted w-5/6"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why CVForge — SEO-rich intro paragraphs */}
      <section className="py-16 px-6" aria-labelledby="why-heading">
        <div className="max-w-4xl mx-auto space-y-6">
          <h2 id="why-heading" className="font-heading text-3xl font-light">
            The <span className="font-semibold">simplest</span> way to create a professional CV online
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Whether you&apos;re applying for your first job in Cape Town or pursuing a senior role in London, a well-designed CV opens doors. <strong>Instant CVForge</strong> is a modern online CV builder crafted to help you produce a clean, ATS-friendly PDF resume without wrestling with Word templates or paying for a subscription. Simply fill in your details, choose from 7 curated templates, pick a font and color scheme, and download your CV as a high-quality PDF — ready to email, print, or upload to any job portal.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Built with South African job seekers in mind (we accept local ZAR payments via PayFast), CVForge works equally well for graduates, mid-career professionals, and executives anywhere in the world. Every CV you generate is rendered server-side using proper typography and embedded fonts so it looks crisp on any device and prints beautifully at A4.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-secondary/30" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto">
          <h2 id="features-heading" className="font-heading text-3xl font-light text-center mb-16">
            Built for <span className="font-semibold">Professionals</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <article className="bg-card border border-border/40 shadow-sm hover:shadow-md transition-shadow rounded-none p-6" data-testid="feature-realtime">
              <Zap className="w-8 h-8 text-accent mb-4" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading text-lg font-medium mb-2">Real-time preview</h3>
              <p className="text-sm text-muted-foreground">See your CV take shape as you type. What you see on screen is exactly what your downloaded PDF looks like.</p>
            </article>
            <article className="bg-card border border-border/40 shadow-sm hover:shadow-md transition-shadow rounded-none p-6" data-testid="feature-templates">
              <FileText className="w-8 h-8 text-accent mb-4" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading text-lg font-medium mb-2">7 professional templates</h3>
              <p className="text-sm text-muted-foreground">Professional, Modern, Creative, Executive, Minimalist, Academic and Technical — each hand-tuned by designers.</p>
            </article>
            <article className="bg-card border border-border/40 shadow-sm hover:shadow-md transition-shadow rounded-none p-6" data-testid="feature-fonts">
              <Type className="w-8 h-8 text-accent mb-4" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading text-lg font-medium mb-2">5 curated fonts</h3>
              <p className="text-sm text-muted-foreground">Helvetica, Georgia, Arial, Times New Roman and Palatino. Embedded properly for razor-sharp rendering.</p>
            </article>
            <article className="bg-card border border-border/40 shadow-sm hover:shadow-md transition-shadow rounded-none p-6" data-testid="feature-colors">
              <Palette className="w-8 h-8 text-accent mb-4" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading text-lg font-medium mb-2">Custom colour schemes</h3>
              <p className="text-sm text-muted-foreground">8 presets plus a full custom picker so your CV reflects your personal brand.</p>
            </article>
            <article className="bg-card border border-border/40 shadow-sm hover:shadow-md transition-shadow rounded-none p-6" data-testid="feature-ai">
              <Sparkles className="w-8 h-8 text-accent mb-4" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading text-lg font-medium mb-2">AI-generated summary</h3>
              <p className="text-sm text-muted-foreground">Let AI write a compelling professional summary based on your experience — first-person, tailored to you.</p>
            </article>
            <article className="bg-card border border-border/40 shadow-sm hover:shadow-md transition-shadow rounded-none p-6" data-testid="feature-drafts">
              <Save className="w-8 h-8 text-accent mb-4" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading text-lg font-medium mb-2">Save multiple versions</h3>
              <p className="text-sm text-muted-foreground">Tailor different CVs for different roles. Load any version on any device with your email and 6-digit code.</p>
            </article>
            <article className="bg-card border border-border/40 shadow-sm hover:shadow-md transition-shadow rounded-none p-6" data-testid="feature-download">
              <Download className="w-8 h-8 text-accent mb-4" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading text-lg font-medium mb-2">Instant PDF download</h3>
              <p className="text-sm text-muted-foreground">Pay once, download immediately. High-quality A4 PDF that prints crisply and works everywhere.</p>
            </article>
            <article className="bg-card border border-border/40 shadow-sm hover:shadow-md transition-shadow rounded-none p-6" data-testid="feature-secure">
              <Shield className="w-8 h-8 text-accent mb-4" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading text-lg font-medium mb-2">Secure ZAR payments</h3>
              <p className="text-sm text-muted-foreground">Processed via PayFast — South Africa&apos;s most trusted payment gateway. International cards accepted.</p>
            </article>
          </div>
        </div>
      </section>

      {/* FAQ Section (matches JSON-LD in index.html) */}
      <section className="py-24 px-6" aria-labelledby="faq-heading">
        <div className="max-w-3xl mx-auto">
          <h2 id="faq-heading" className="font-heading text-3xl font-light text-center mb-12">
            Frequently asked <span className="font-semibold">questions</span>
          </h2>
          <dl className="space-y-8">
            {faqs.map((item, i) => (
              <div key={i}>
                <dt className="font-heading text-lg font-medium mb-2">{item.q}</dt>
                <dd className="text-sm text-muted-foreground leading-relaxed">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-secondary/30" aria-labelledby="cta-heading">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <h2 id="cta-heading" className="font-heading text-3xl sm:text-4xl font-light">
            Ready to <span className="font-semibold">stand out?</span>
          </h2>
          <p className="text-muted-foreground">One-time payment. Instant download. No subscription.</p>
          <Button
            data-testid="cta-start-building"
            onClick={() => navigate("/builder")}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none h-12 px-8 font-medium tracking-wide transition-transform hover:-translate-y-0.5 active:scale-95"
          >
            Start Building Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border/40 bg-secondary/20" role="contentinfo">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center">
              <img src="/logo.svg" alt="Instant CVForge logo" className="w-6 h-6 mr-2" width="24" height="24" />
              <span className="text-sm text-muted-foreground">
                &copy; {new Date().getFullYear()} Instant CVForge. All rights reserved.
              </span>
            </div>
            <nav aria-label="Footer" className="flex gap-6 text-sm">
              <Link 
                to="/refund-policy" 
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="refund-policy-link"
              >
                Refund & Cancellation Policy
              </Link>
              <a 
                href="mailto:support@instantcvforge.com" 
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Contact Support
              </a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
